// Static Dataleo corporate content - brand, capability clusters, decision
// flow taxonomy. This lives here (not in DB) because it's part of the brand
// architecture. Editorial content (Radar / Labs / Clients) is DB-driven.

export const BRAND = {
  primary: "Supply Chain Decision Systems",
  territory: "Supply Chain. Technology. Decision Systems.",
  conviction: {
    en: "Companies don't need more technology. They need better decision systems.",
    fr: "Les entreprises n'ont pas besoin de plus de technologie. Elles ont besoin de meilleurs systèmes de décision.",
  },
  editorial: {
    en: "The tool is not the product. The decision flow is.",
    fr: "L'outil n'est pas le produit. Le flux de décision l'est.",
  },
};

// APS platforms Dataleo has senior client-side experience on. Rendered as
// credibility chips (NOT a logo wall) on Delivery capability + Home.
export const APS_PLATFORMS = ["o9", "Kinaxis", "Anaplan", "SAP APO / IBP", "OMP", "FuturMaster", "Intuiflow"];

// -----------------------------------------------------------------------
// CAPABILITY PILLARS - clustered structure
// Instead of a flat checkbox list, each pillar is split into 3-4 clusters
// that read like a real practice area. This is the key visual/UX shift
// from "consulting website" to "boutique operating model".
// -----------------------------------------------------------------------
export const CAPABILITIES = [
  {
    slug: "advisory",
    tag: "DEFINE",
    title: { en: "Advisory & Transformation", fr: "Advisory & Transformation" },
    tagline: { en: "Define the system.", fr: "Définir le système." },
    intro: {
      en: "We help you define the right Supply Chain processes, decisions, operating model and transformation roadmap. The starting point is always the decision: who decides, based on what information, at what frequency, for what action, with what level of automation.",
      fr: "Nous aidons à définir les bons processus Supply Chain, les décisions, le modèle opérationnel et la roadmap de transformation. Le point de départ est toujours la décision : qui décide, sur quelle information, à quelle fréquence, pour quelle action, avec quel niveau d'automatisation.",
    },
    clusters: [
      {
        code: "01",
        name: { en: "Planning & S&OP", fr: "Planning & S&OP" },
        items: ["S&OP / IBP", "Demand Planning", "Supply Planning", "Inventory"],
      },
      {
        code: "02",
        name: { en: "Transformation & Operating Model", fr: "Transformation & Modèle opérationnel" },
        items: ["Supply Chain strategy", "Target Operating Model", "Process design", "Business / IT alignment"],
      },
      {
        code: "03",
        name: { en: "Planning Technology Strategy", fr: "Stratégie Planning Technology" },
        items: ["APS transformation", "APS selection", "MAP / MFP", "Vendor / SI selection", "RFP support"],
      },
      {
        code: "04",
        name: { en: "Decision Architecture", fr: "Architecture de décision" },
        items: ["Decision framing", "Governance", "Decision rights", "Transformation roadmap"],
      },
    ],
  },
  {
    slug: "delivery",
    tag: "DELIVER",
    title: { en: "Technology & Delivery", fr: "Technologie & Delivery" },
    tagline: { en: "Make it work.", fr: "Le faire fonctionner." },
    intro: {
      en: "A core Dataleo capability. We lead the full lifecycle - DEFINE → DESIGN → DELIVER → RUN → IMPROVE - with senior client-side leadership across business, IT, vendors and system integrators.",
      fr: "Une capacité centrale de Dataleo. Nous menons le cycle de vie complet - DEFINE → DESIGN → DELIVER → RUN → IMPROVE - avec un leadership client-side senior sur le business, l'IT, les éditeurs et les intégrateurs.",
    },
    clusters: [
      {
        code: "01",
        name: { en: "APS & Planning Technology", fr: "APS & Planning Technology" },
        items: APS_PLATFORMS,
        variant: "aps",
      },
      {
        code: "02",
        name: { en: "Program & Project Management", fr: "Gestion de programme & projet" },
        items: ["Program Management", "Project Management", "PMO / Delivery Office", "Delivery governance"],
      },
      {
        code: "03",
        name: { en: "Data & Integration", fr: "Data & Intégration" },
        items: ["Data Management", "Integration coordination", "Architecture coordination", "Business / IT alignment"],
      },
      {
        code: "04",
        name: { en: "Testing, Deployment & Cutover", fr: "Tests, déploiement & cutover" },
        items: ["SIT / UAT", "Deployment", "Cutover", "Hypercare", "Vendor / SI coordination"],
      },
    ],
    run: {
      title: { en: "Application Management & Continuous Improvement", fr: "TMA & Amélioration continue" },
      tagline: { en: "From transformation to RUN.", fr: "De la transformation au RUN." },
      body: {
        en: "Not basic IT maintenance. The natural continuation of a Supply Chain transformation - preserving knowledge and continuously improving the decision system after go-live.",
        fr: "Pas de la simple maintenance IT. La continuation naturelle d'une transformation Supply Chain - préserver la connaissance et améliorer continuellement le système de décision après go-live.",
      },
      items: [
        "Platform ownership support", "TMA / AMS", "Service governance",
        "Functional & data support", "Release management",
        "Enhancement backlog", "Vendor governance", "Roadmap & continuous improvement",
      ],
    },
  },
  {
    slug: "ai-build",
    tag: "BUILD",
    title: { en: "AI & Decision Build", fr: "IA & Decision Build" },
    tagline: { en: "Build what is missing.", fr: "Construire ce qui manque." },
    intro: {
      en: "We rapidly build, test and industrialise focused Decision Apps around enterprise systems. The value is often to build the missing decision layer - around, between or on top of existing systems - not to replace them.",
      fr: "Nous construisons, testons et industrialisons rapidement des Decision Apps ciblées autour des systèmes d'entreprise. La valeur est souvent de construire la couche de décision manquante - autour, entre ou au-dessus des systèmes existants - pas de les remplacer.",
    },
    principle: {
      en: "Build fast. Govern properly. Scale only what works.",
      fr: "Construire vite. Gouverner correctement. N'industrialiser que ce qui marche.",
    },
    supportingStatement: {
      en: "A prototype is easy. A system you can trust is not.",
      fr: "Un prototype, c'est facile. Un système auquel on peut se fier, non.",
    },
    clusters: [
      {
        code: "01",
        name: { en: "Decision Apps", fr: "Decision Apps" },
        items: ["Decision framing", "Controlled prototyping", "AI-built internal tools", "Industrialization"],
      },
      {
        code: "02",
        name: { en: "AI & Optimisation", fr: "IA & Optimisation" },
        items: ["Forecasting / ML", "Optimization", "Agents", "Human-in-the-loop"],
      },
      {
        code: "03",
        name: { en: "Build stack", fr: "Stack technique" },
        items: ["Python applications", "APIs", "Low-code", "Workflow automation", "Power BI"],
      },
      {
        code: "04",
        name: { en: "Governance & Validation", fr: "Gouvernance & Validation" },
        items: ["Model validation", "Data lineage", "Auditability", "Decision governance"],
      },
    ],
  },
];

export const CORPORATE_NAV = [
  { slug: "home", to: "/", en: "Home", fr: "Accueil", exact: true },
  { slug: "capabilities", to: "/capabilities", en: "Capabilities", fr: "Capacités" },
  { slug: "labs", to: "/labs", en: "Labs", fr: "Labs" },
  { slug: "radar", to: "/radar", en: "Radar", fr: "Radar" },
  { slug: "clients", to: "/clients", en: "Clients", fr: "Clients" },
  { slug: "partners", to: "/partners", en: "Partners", fr: "Partenaires" },
  { slug: "about", to: "/about", en: "About", fr: "À propos" },
];
